import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Button,
  Link,
  Alert,
  IconButton,
  // Tooltip,
  Paper,
  Stack,
  Avatar,
} from '@mui/material';

import {
  ArrowBack as ArrowBackIcon,
  // Schedule as ScheduleIcon,
  // Person as PersonIcon,
  // Group as GroupIcon,
  // Link as LinkIcon,
  ContentCopy as ContentCopyIcon,
  // Email as EmailIcon,
  // Phone as PhoneIcon,
  // Place as PlaceIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';

import { useParams, useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import { useGetMeetingByIdQuery } from '../../store/api';
import { toast } from 'react-toastify';
import { CALENDAR_SCHEDULE_PATH } from '../../navigations/path';

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return 'N/A';
  try {
    const date = new Date(dateTimeString.replace(' ', 'T') + 'Z');
    return date.toLocaleString();
  } catch {
    return dateTimeString;
  }
};

const getStatusColor = (statusName) => {
  const map = {
    Upcoming: 'primary',
    Completed: 'success',
    Cancelled: 'error',
    Rescheduled: 'warning',
    'In Progress': 'info',
  };
  return map[statusName] || 'default';
};

const InfoRow = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: 500 }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

const MeetingView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    data: meetingResponse,
    isLoading: loading,
    isError,
    error: queryError,
  } = useGetMeetingByIdQuery(id, { skip: !id });
  const currentMeeting = meetingResponse?.data ?? meetingResponse ?? null;
  const error = isError
    ? (queryError?.data?.message || queryError?.message || 'Failed to load meeting')
    : null;

  useEffect(() => {
    if (isError) {
      toast.error(error);
    }
  }, [isError, error]);

  const handleBack = () => navigate(CALENDAR_SCHEDULE_PATH);

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied');
  };

  if (loading && !currentMeeting) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (error && !currentMeeting) {
    return (
      <PageLayout>
        <Container sx={{ py: 4 }}>
          <Alert severity="error">{error}</Alert>
          <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Back
          </Button>
        </Container>
      </PageLayout>
    );
  }

  const meeting = currentMeeting;
  if (!meeting) return null;

  const lead = meeting.lead || {};
  const status = meeting.status || {};
  const attendees = meeting.attendees || [];

  return (
    <PageLayout>
      <Container maxWidth="lg" sx={{ py: 3 }}>

        {/* Top bar */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Button startIcon={<ArrowBackIcon />} variant="outlined" onClick={handleBack}>
            Back
          </Button>

          <Chip
            label={status.name || 'Unknown'}
            color={getStatusColor(status.name)}
            sx={{ fontWeight: 600 }}
          />
        </Box>

        {/* HERO HEADER */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 3,
            borderRadius: 5,
            border: '1px solid',
            borderColor: 'divider',
            background:
              'linear-gradient(135deg, rgba(25,118,210,0.08), rgba(25,118,210,0.02))',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
              <CalendarMonthIcon />
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Meeting Details
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDateTime(meeting.start_date_time)} →{' '}
                {formatDateTime(meeting.end_date_time)}
              </Typography>
            </Box>

            <Typography variant="body1" color="text.secondary">
              TZ: {meeting.time_zone || '—'}
            </Typography>
          </Stack>
        </Paper>

        {/* MAIN GRID */}
        <Grid container spacing={3}>

          {/* LEFT: Meeting */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 4 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Meeting Info
                </Typography>

                <Stack spacing={2}>
                  <InfoRow label="Start" value={formatDateTime(meeting.start_date_time)} />
                  <InfoRow label="End" value={formatDateTime(meeting.end_date_time)} />
                  <InfoRow label="Created" value={meeting.created_at} />
                </Stack>

                {meeting.description && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {meeting.description}
                    </Typography>
                  </>
                )}

                {meeting.google_event_url && (
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Link href={meeting.google_event_url} target="_blank">
                      Open Calendar
                    </Link>
                    <IconButton onClick={() => handleCopyLink(meeting.google_event_url)}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* RIGHT: Lead */}
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 4 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Lead
                </Typography>

                <Typography variant="subtitle1" fontWeight={700}>
                  {lead.name || '—'}
                </Typography>

                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  {lead.email && <InfoRow label="Email" value={lead.email} />}
                  {lead.phone && <InfoRow label="Phone" value={lead.phone} />}
                  {lead.address && <InfoRow label="Address" value={lead.address} />}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* BOTTOM: Attendees */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 4 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Attendees ({attendees.length})
                </Typography>

                {attendees.length === 0 ? (
                  <Typography color="text.secondary">No attendees</Typography>
                ) : (
                  <List>
                    {attendees.map((a) => (
                      <ListItem key={a.id} divider>
                        <ListItemText
                          primary={<b>{a.name || a.username}</b>}
                          secondary={`${a.email || ''} ${a.phone || ''}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

        </Grid>
      </Container>
    </PageLayout>
  );
};

export default MeetingView;